// Public page: rendered without authentication (see App.js), because store
// listings (Google Play / Chrome Web Store) require a privacy policy URL
// that is reachable by anyone.
const styles = {
    // The app's global stylesheet paints a dark background, so the page
    // carries its own full-height light backdrop to stay readable.
    backdrop: { minHeight: "100vh", background: "#ffffff" },
    page: {
        maxWidth: "860px",
        margin: "0 auto",
        padding: "48px 24px 96px",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        color: "#1f2937",
        lineHeight: 1.7,
    },
    h1: { fontSize: "2rem", marginBottom: "4px", color: "#111827" },
    updated: { color: "#6b7280", marginBottom: "32px" },
    h2: { fontSize: "1.25rem", marginTop: "36px", marginBottom: "12px", color: "#111827" },
};

function PrivacyPolicy() {
    return (
        <div style={styles.backdrop}>
        <div style={styles.page}>
            <h1 style={styles.h1}>Royal Motors Privacy Policy</h1>
            <p style={styles.updated}>Last updated: July 30, 2026</p>

            <p>
                This Privacy Policy describes how Royal Motors ("we", "us", or "our") collects, uses, and
                discloses your personal information when you visit or use our services at{" "}
                <a href="https://royalmotors.ge">royalmotors.ge</a> (the "Site") or otherwise communicate with us
                (collectively, the "Services"). For purposes of this Privacy Policy, "you" and "your" means
                you as the user of the Services, whether you are a customer, website visitor, or another
                individual whose information we have collected pursuant to this Privacy Policy.
            </p>
            <p>
                Please read this Privacy Policy carefully. By using and accessing any of the Services, you
                agree to the collection, use, and disclosure of your information as described in this
                Privacy Policy. If you do not agree to this Privacy Policy, please do not use or access any
                of the Services.
            </p>

            <h2 style={styles.h2}>Changes to This Privacy Policy</h2>
            <p>
                We may update this Privacy Policy from time to time, including to reflect changes to our
                practices or for other operational, legal, or regulatory reasons. We will post the revised
                Privacy Policy on the Site, update the "Last updated" date and take any other steps required
                by applicable law.
            </p>

            <h2 style={styles.h2}>How We Collect and Use Your Personal Information</h2>
            <p>
                To provide the Services, we collect personal information about you from a variety of
                sources, as set out below. The information that we collect and use varies depending on how
                you interact with us.
            </p>
            <p>
                In addition to the specific uses set out below, we may use information we collect about you
                to communicate with you, provide the Services, comply with any applicable legal obligations,
                enforce any applicable terms of service, and to protect or defend the Services, our rights,
                and the rights of our users or others.
            </p>

            <h2 style={styles.h2}>What Personal Information We Collect</h2>
            <p>
                The types of personal information we obtain about you depend on how you interact with our
                Site and use our Services. When we use the term "personal information", we are referring to
                information that identifies, relates to, describes or can be associated with you.
            </p>

            <h2 style={styles.h2}>Information We Collect Directly from You</h2>
            <p>Information that you directly submit to us through our Services may include:</p>
            <ul>
                <li>Basic contact details including your name, address, phone number, and email.</li>
                <li>Account information including your username, password, and security questions.</li>
                <li>
                    Usage information including the vehicles and auction lots you view, watch, bid on, or
                    add to your watchlist.
                </li>
                <li>
                    Customer support information including the information you choose to include in
                    communications with us, for example, when sending a message through the Services.
                </li>
            </ul>
            <p>
                Some features of the Services may require you to directly provide us with certain
                information about yourself. You may elect not to provide this information, but doing so may
                prevent you from using or accessing these features.
            </p>

            <h2 style={styles.h2}>Information We Collect through Cookies</h2>
            <p>
                We also automatically collect certain information about your interaction with the Services
                ("Usage Data"). To do this, we may use cookies and similar technologies ("Cookies"). Usage
                Data may include information about how you access and use our Site and your account,
                including device information, browser information, information about your network
                connection, your IP address and other information regarding your interaction with the
                Services.
            </p>
            <p>
                We use Cookies to power and improve our Site and our Services (including to remember your
                actions and preferences, such as keeping you signed in), to run analytics and better
                understand user interaction with the Services. Most browsers automatically accept Cookies by
                default, but you can choose to set your browser to remove or reject Cookies through your
                browser controls. Please keep in mind that removing or blocking Cookies can negatively
                impact your user experience and may cause some of the Services, including certain features
                and general functionality, to work incorrectly or no longer be available.
            </p>

            <h2 style={styles.h2}>Information We Obtain from Third Parties</h2>
            <p>
                We may obtain information about you from third parties, including from vendors and service
                providers who may collect information on our behalf, such as companies who support our Site
                and Services (for example hosting, IT and analytics providers). Any information we obtain
                from third parties will be treated in accordance with this Privacy Policy. We are not
                responsible or liable for the accuracy of the information provided to us by third parties
                and are not responsible for any third party's policies or practices.
            </p>

            <h2 style={styles.h2}>How We Use Your Personal Information</h2>
            <ul>
                <li>
                    <strong>Providing the Services.</strong> We use your personal information to provide you
                    with the Services in order to perform our contract with you, including to create,
                    maintain and otherwise manage your account, to send notifications to you related to your
                    account and activity, and to operate the auction-related features of the Services.
                </li>
                <li>
                    <strong>Security and Fraud Prevention.</strong> We use your personal information to
                    detect, investigate or take action regarding possible fraudulent, illegal or malicious
                    activity. If you choose to use the Services and register an account, you are responsible
                    for keeping your account credentials safe. We highly recommend that you do not share
                    your username, password, or other access details with anyone else. If you believe your
                    account has been compromised, please contact us immediately.
                </li>
                <li>
                    <strong>Communicating with You.</strong> We use your personal information to provide you
                    with customer support and improve our Services. This is in our legitimate interests in
                    order to be responsive to you, to provide effective services to you, and to maintain our
                    business relationship with you.
                </li>
            </ul>

            <h2 style={styles.h2}>How We Disclose Personal Information</h2>
            <p>
                In certain circumstances, we may disclose your personal information to third parties for
                legitimate purposes subject to this Privacy Policy. Such circumstances may include:
            </p>
            <ul>
                <li>
                    With vendors or other third parties who perform services on our behalf (e.g., IT
                    management, data analytics, customer support, cloud storage).
                </li>
                <li>
                    When you direct, request us or otherwise consent to our disclosure of certain
                    information to third parties, with your consent.
                </li>
                <li>
                    In connection with a business transaction such as a merger, to comply with any
                    applicable legal obligations (including to respond to subpoenas, search warrants and
                    similar requests), to enforce any applicable terms of service, and to protect or defend
                    the Services, our rights, and the rights of our users or others.
                </li>
            </ul>
            <p>
                We do not sell your personal information, and we do not use or disclose sensitive personal
                information for the purposes of inferring characteristics about you.
            </p>

            <h2 style={styles.h2}>Third Party Websites and Links</h2>
            <p>
                Our Site may provide links to websites or other online platforms operated by third parties.
                If you follow links to sites not affiliated or controlled by us, you should review their
                privacy and security policies and other terms and conditions. We do not guarantee and are
                not responsible for the privacy or security of such sites, including the accuracy,
                completeness, or reliability of information found on these sites.
            </p>

            <h2 style={styles.h2}>Children's Data</h2>
            <p>
                The Services are not intended to be used by children, and we do not knowingly collect any
                personal information about children. If you are the parent or guardian of a child who has
                provided us with their personal information, you may contact us using the contact details
                set out below to request that it be deleted.
            </p>

            <h2 style={styles.h2}>Security and Retention of Your Information</h2>
            <p>
                Please be aware that no security measures are perfect or impenetrable, and we cannot
                guarantee "perfect security." In addition, any information you send to us may not be secure
                while in transit. We recommend that you do not use unsecure channels to communicate
                sensitive or confidential information to us.
            </p>
            <p>
                We retain your personal information for as long as necessary to provide the Services, comply
                with our legal obligations, resolve disputes, and enforce our agreements.
            </p>

            <h2 style={styles.h2}>Your Rights</h2>
            <p>
                Depending on where you live, you may have rights in relation to your personal information,
                such as the right to access, correct, or delete the personal information we hold about you.
                To exercise any of these rights, contact us using the details below.
            </p>

            <h2 style={styles.h2}>Contact</h2>
            <p>
                Should you have any questions about our privacy practices or this Privacy Policy, or if you
                would like to exercise any of the rights available to you, please email us at{" "}
                <a href="mailto:info@carexpress.ge">info@carexpress.ge</a>.
            </p>
        </div>
        </div>
    );
}

export default PrivacyPolicy;
